/**
 * @voltxampere/web — LED glow overlay (Sprint 31-6)
 *
 * Paints LED brightness halos onto the fx canvas (the same overlay
 * Sprint 29 meteor particles + Sprint 30 heatmap/wire-gradient share).
 * Each LED's halo is a radial gradient sized by `brightness` (Sprint
 * 31-2 log-scale formula) and tinted by the LED's color enum.
 *
 * Layer order in vxa-canvas tick:
 *   1. Heatmap halos (Sprint 30-3)
 *   2. Wire color stroke (Sprint 30-4)
 *   3. Meteor particles + wire glow (Sprint 29-5)
 *   4. LED glow halos (Sprint 31-6) — drawn LAST so emission overrides
 *      the colder heatmap blue underneath at the LED position
 *
 * `screen` blend (additive) keeps the glow warm against the void
 * background and brightens naturally where overlapping with heatmap.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { ComponentInstance } from "@voltxampere/schema";
import type { ComponentFxState } from "@voltxampere/fx";

const MIN_RADIUS = 12;
const MAX_RADIUS = 36;

export interface LedOverlayDrawOpts {
  readonly viewport: {
    readonly center: { x: number; y: number };
    readonly zoom: number;
  };
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly dpr: number;
  /** All LED components from the active circuit (id → ComponentInstance). */
  readonly leds: ReadonlyMap<string, ComponentInstance>;
  /** Per-component fx state (FxFrame.components map). */
  readonly fxStates: ReadonlyMap<string, ComponentFxState>;
}

/**
 * Paint LED brightness halos. No-op when there are no LEDs or every
 * LED has brightness 0.
 */
export function drawLedOverlay(ctx: CanvasRenderingContext2D, opts: LedOverlayDrawOpts): void {
  if (opts.leds.size === 0) return;

  ctx.save();
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  const z = opts.viewport.zoom;
  ctx.translate(opts.cssWidth / 2, opts.cssHeight / 2);
  ctx.scale(z, z);
  ctx.translate(-opts.viewport.center.x, -opts.viewport.center.y);

  ctx.globalCompositeOperation = "screen";

  for (const [id, led] of opts.leds) {
    const fx = opts.fxStates.get(id);
    if (fx?.type !== "led") continue;
    const brightness = fx.brightness ?? 0;
    if (brightness <= 0) continue;
    const colorHex = fx.color ?? "#FF3344";
    paintLedHalo(ctx, led.position.x, led.position.y, brightness, colorHex);
  }

  ctx.globalCompositeOperation = "source-over";
  ctx.restore();
}

function paintLedHalo(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  brightness: number,
  colorHex: string,
): void {
  const radius = MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * brightness;
  const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
  grad.addColorStop(0, hexWithAlpha(colorHex, brightness));
  grad.addColorStop(0.5, hexWithAlpha(colorHex, brightness * 0.4));
  grad.addColorStop(1, hexWithAlpha(colorHex, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
}

function hexWithAlpha(hex: string, alpha: number): string {
  // #FF3344 → rgba(255, 51, 68, alpha)
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${String(r)},${String(g)},${String(b)},${a.toFixed(3)})`;
}
